/**
 * QuotesListView (/quotes) — the office "Proposals" surface.
 *
 * PROPOSAL-VISION-02 (Exhibit C, Seth approved 2026-10-13): proposals read as
 * a PIPELINE, not a list — lanes (Draft · Sent · Viewed · Won · Closed),
 * oldest-waiting first in the open lanes, every card one decision. A sent or
 * viewed proposal going stale (5+ days) wears amber; a declined one carries
 * the homeowner's own REASON (QUOTE-DECLINE-01), so a no teaches the next
 * quote. The classic table stays one toggle away.
 * Brought over from Seth's proposal-app "PROPOSALS" admin tab
 * (QUOTES-ADMIN-CONSOLE, BACKLOG §N 2026-06-29).
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, Search, ChevronRight, Clock, Eye, CheckCircle2, XCircle, FileEdit, Inbox, LayoutGrid, Rows3, MessageSquare,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/ui/PageHeader";
import {
  useQuoteList, displayStatus, cleanCustomerName, QUOTE_STATUS_LABEL, type QuoteListRow,
} from "./quotes";

const money = (n: number) =>
  (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const dateUS = (s: string | null) =>
  s ? new Date(s).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "—";

const STATUS_STYLE: Record<string, { chip: string; dot: string; icon: React.ComponentType<{ className?: string }> }> = {
  draft: { chip: "bg-slate-100 text-slate-600", dot: "bg-slate-400", icon: FileEdit },
  sent: { chip: "bg-blue-50 text-blue-700", dot: "bg-blue-500", icon: Clock },
  viewed: { chip: "bg-violet-50 text-violet-700", dot: "bg-violet-500", icon: Eye },
  accepted: { chip: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500", icon: CheckCircle2 },
  declined: { chip: "bg-rose-50 text-rose-700", dot: "bg-rose-500", icon: XCircle },
  expired: { chip: "bg-amber-50 text-amber-700", dot: "bg-amber-500", icon: Clock },
};

const FILTERS = ["all", "draft", "sent", "viewed", "accepted", "declined", "expired"] as const;
type Filter = (typeof FILTERS)[number];

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.draft;
  const Icon = s.icon;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold", s.chip)}>
      <Icon className="h-3.5 w-3.5" />
      {QUOTE_STATUS_LABEL[status] ?? status}
    </span>
  );
}

type Row = QuoteListRow & { disp: string; name: string };

/** Days a proposal has been WAITING on the customer (sent-or-created → now). */
function waitingDays(r: Row): number {
  const t = r.sent_at ?? r.created_at;
  return Math.max(0, Math.floor((Date.now() - new Date(t).getTime()) / 864e5));
}
const STALE_DAYS = 5;

/* ── the pipeline lanes ─────────────────────────────────────────────────── */

const LANES: Array<{ key: string; label: string; of: (r: Row) => boolean; openLane: boolean }> = [
  { key: "draft", label: "Draft", of: (r) => r.disp === "draft", openLane: false },
  { key: "sent", label: "Sent", of: (r) => r.disp === "sent", openLane: true },
  { key: "viewed", label: "Viewed", of: (r) => r.disp === "viewed", openLane: true },
  { key: "accepted", label: "Won", of: (r) => r.disp === "accepted", openLane: false },
  { key: "closed", label: "Closed", of: (r) => r.disp === "declined" || r.disp === "expired", openLane: false },
];

function LaneCard({ r, openLane, onOpen }: { r: Row; openLane: boolean; onOpen: () => void }) {
  const days = waitingDays(r);
  const stale = openLane && days >= STALE_DAYS;
  // QUOTE-ASK-01: a homeowner question nobody has answered outranks "stale".
  const asks = Number(r.open_questions ?? 0);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "w-full rounded-xl border bg-white px-3 py-2.5 text-left transition-colors hover:border-brand-300",
        asks > 0 ? "border-amber-400 bg-amber-50 ring-1 ring-amber-200" : stale ? "border-amber-300 bg-amber-50/50" : "border-hairline",
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 truncate text-[13px] font-bold text-slate-800">{r.name}</span>
        <span className="shrink-0 text-[12.5px] font-bold tabular-nums text-slate-700">{money(r.headline_total)}</span>
      </div>
      <div className="truncate text-[11px] text-slate-400">{r.title || "Comfort proposal"}{r.dispatch_no ? ` · #${r.dispatch_no}` : ""}</div>
      {asks > 0 && (
        <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-200/70 px-2 py-0.5 text-[11px] font-bold text-amber-900">
          <MessageSquare className="h-3 w-3" /> {asks === 1 ? "question waiting" : `${asks} questions waiting`}
        </div>
      )}
      {openLane && (
        <div className={cn("mt-1 text-[11px] font-semibold", stale ? "text-amber-700" : "text-slate-400")}>
          {days === 0 ? "today" : `waiting ${days} day${days === 1 ? "" : "s"}`}
          {stale ? " — time for a check-in call" : ""}
          {/* QUOTE-POLISH-05: a homeowner who keeps coming back is warm — say so. */}
          {Number(r.view_count ?? 0) > 1 && (
            <span className="ml-1.5 rounded-full bg-violet-50 px-1.5 py-px text-[10.5px] font-bold text-violet-700" title={r.last_viewed_at ? `Last opened ${new Date(r.last_viewed_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : undefined}>
              opened {r.view_count}×
            </span>
          )}
        </div>
      )}
      {r.disp === "accepted" && (
        <div className="mt-1 text-[11px] font-semibold text-emerald-700">accepted {dateUS(r.accepted_at)}{r.job_id ? " · job created ✓" : ""}</div>
      )}
      {r.disp === "declined" && (
        <div className="mt-1 truncate text-[11px] font-semibold text-rose-600" title={r.declined_reason ?? undefined}>
          {r.declined_reason ? `“${r.declined_reason}”` : "declined"}
        </div>
      )}
      {r.disp === "expired" && <div className="mt-1 text-[11px] font-semibold text-amber-700">expired — Extend or Re-bid from the quote</div>}
    </button>
  );
}

function Lanes({ rows, onOpen }: { rows: Row[]; onOpen: (id: string) => void }) {
  return (
    <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
      {LANES.map((lane) => {
        const items = rows.filter(lane.of).sort((a, b) =>
          lane.openLane
            ? new Date(a.sent_at ?? a.created_at).getTime() - new Date(b.sent_at ?? b.created_at).getTime() // longest-waiting first
            : new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        );
        return (
          <div key={lane.key} className="rounded-2xl border border-hairline bg-slate-50/60 p-2.5">
            <div className="mb-2 flex items-baseline justify-between px-1">
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">{lane.label}</span>
              <span className="text-[13px] font-extrabold tabular-nums text-slate-700">{items.length}</span>
            </div>
            <div className="space-y-2">
              {items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-hairline px-3 py-4 text-center text-[11px] text-slate-300">empty</div>
              ) : (
                items.map((r) => <LaneCard key={r.id} r={r} openLane={lane.openLane} onOpen={() => onOpen(r.id)} />)
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function QuotesListView() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuoteList();
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  // Lanes are the default read (Exhibit C); the table is one toggle away.
  const [view, setView] = useState<"lanes" | "list">("lanes");

  const rows = useMemo(
    () =>
      (data ?? []).map((r: QuoteListRow) => ({
        ...r,
        disp: displayStatus(r),
        name: cleanCustomerName(r.customer_name),
      })),
    [data],
  );

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length };
    for (const f of FILTERS) if (f !== "all") c[f] = rows.filter((r) => r.disp === f).length;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (view === "list" && filter !== "all" && r.disp !== filter) return false;
      if (!needle) return true;
      return (
        r.name.toLowerCase().includes(needle) ||
        (r.dispatch_no ?? "").includes(needle) ||
        (r.title ?? "").toLowerCase().includes(needle)
      );
    });
  }, [rows, filter, q, view]);

  return (
    <div className="mx-auto max-w-7xl px-6 py-6">
      {/* Header */}
      <PageHeader
        icon={FileText}
        title="Proposals"
        description="Every Good / Better / Best quote — open one to edit or present it."
        actions={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search customer, dispatch #…"
                className="w-64 rounded-full border border-hairline bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
            </div>
            <div className="inline-flex overflow-hidden rounded-full border border-hairline">
              {([["lanes", LayoutGrid], ["list", Rows3]] as const).map(([v, Icon]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  title={v === "lanes" ? "Pipeline lanes" : "Table"}
                  className={cn("grid h-9 w-10 place-items-center transition-colors", view === v ? "bg-brand-50 text-brand-700" : "bg-white text-slate-400 hover:text-slate-600")}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
        }
      />

      {/* QUOTE-LIFECYCLE-01: the pipeline at a glance — open $, won $, close rate. */}
      {(() => {
        const open = rows.filter((r) => r.disp === "sent" || r.disp === "viewed");
        const won = rows.filter((r) => r.disp === "accepted");
        const decided = won.length + rows.filter((r) => r.disp === "declined" || r.disp === "expired").length;
        const sum = (xs: typeof rows) => xs.reduce((s, r) => s + Number(r.headline_total || 0), 0);
        const rate = decided > 0 ? Math.round((won.length / decided) * 100) : null;
        const stale = open.filter((r) => waitingDays(r) >= STALE_DAYS).length;
        const Cell = ({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) => (
          <div className="min-w-[9rem] flex-1 rounded-xl border border-hairline bg-white px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
            <div className={cn("mt-0.5 text-xl font-bold tabular-nums", tone ?? "text-slate-800")}>{value}</div>
            {sub && <div className="text-xs text-slate-400">{sub}</div>}
          </div>
        );
        return (
          <div className="mb-4 flex flex-wrap gap-2.5">
            <Cell label="Open Proposals" value={money(sum(open))} sub={`${open.length} awaiting an answer${stale > 0 ? ` · ${stale} getting stale` : ""}`} tone={stale > 0 ? "text-amber-700" : undefined} />
            <Cell label="Won" value={money(sum(won))} sub={`${won.length} accepted`} tone="text-emerald-700" />
            <Cell label="Close Rate" value={rate == null ? "—" : `${rate}%`} sub={decided > 0 ? `of ${decided} decided` : "nothing decided yet"} tone={rate != null && rate >= 40 ? "text-emerald-700" : "text-slate-800"} />
          </div>
        );
      })()}

      {isLoading && <div className="rounded-2xl border border-hairline bg-white px-4 py-10 text-center text-sm text-slate-400">Loading proposals…</div>}
      {error && !isLoading && (
        <div className="rounded-2xl border border-hairline bg-white px-4 py-10 text-center text-sm text-rose-500">Couldn’t load proposals. {(error as Error)?.message}</div>
      )}
      {!isLoading && !error && rows.length === 0 && (
        <div className="rounded-2xl border border-hairline bg-white px-4 py-12 text-center">
          <Inbox className="mx-auto mb-2 h-7 w-7 text-slate-300" />
          <div className="text-sm font-medium text-slate-500">No proposals yet</div>
          <div className="mt-1 text-xs text-slate-400">Build one from a customer’s record or a service call (Build a proposal).</div>
        </div>
      )}

      {!isLoading && !error && rows.length > 0 && view === "lanes" && (
        <Lanes rows={filtered} onOpen={(id) => navigate(`/quotes/${id}`)} />
      )}

      {!isLoading && !error && rows.length > 0 && view === "list" && (
        <>
          {/* Status filter chips (table mode) */}
          <div className="mb-4 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
                  filter === f
                    ? "border-brand-600 bg-brand-600 text-white"
                    : "border-hairline bg-white text-slate-600 hover:border-brand-300 hover:text-brand-700",
                )}
              >
                {f === "all" ? "All" : QUOTE_STATUS_LABEL[f]}
                <span
                  className={cn(
                    "rounded-full px-1.5 text-[10px] font-bold tabular-nums",
                    filter === f ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500",
                  )}
                >
                  {counts[f] ?? 0}
                </span>
              </button>
            ))}
          </div>

          <div className="overflow-hidden rounded-2xl border border-hairline bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-slate-50/70 text-left text-[12px] font-semibold text-slate-500">
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-center">Options</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Dispatch</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <Inbox className="mx-auto mb-2 h-7 w-7 text-slate-300" />
                      <div className="text-sm font-medium text-slate-500">No proposals match this filter</div>
                      <div className="mt-1 text-xs text-slate-400">Try a different status or clear the search.</div>
                    </td>
                  </tr>
                )}
                {filtered.map((r, i) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/quotes/${r.id}`)}
                    className={cn(
                      "group cursor-pointer border-b border-hairline/70 transition-colors last:border-0 hover:bg-brand-50/40",
                      i % 2 ? "bg-slate-50/30" : "bg-white",
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{r.name}</div>
                      <div className="text-xs text-slate-400">{r.title || "Comfort proposal"}</div>
                      {r.disp === "declined" && r.declined_reason && (
                        <div className="mt-0.5 text-xs font-medium text-rose-600">“{r.declined_reason}”</div>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={r.disp} /></td>
                    <td className="px-4 py-3 text-center tabular-nums text-slate-500">{r.option_count}</td>
                    <td className="px-4 py-3 text-right font-bold tabular-nums text-slate-900">{money(r.headline_total)}</td>
                    <td className="px-4 py-3">
                      {r.dispatch_no ? (
                        <span className="font-mono text-xs text-slate-500">#{r.dispatch_no}</span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{dateUS(r.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="ml-auto h-4 w-4 text-slate-300 transition-colors group-hover:text-brand-600" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
